import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@jsverse/transloco';
import { take } from 'rxjs';

export enum SnackBarTypeEnum {
    ERROR = 'error',
    INFO = 'info',
    SUCCESS = 'success',
    WARNING = 'warning',
}

@Injectable({
    providedIn: 'root',
})
export class SnackBarService {
    private static minimalDuration = 2000; // in milliseconds
    private static readingRatePerSecond = 60 / 140; // slow reader can read 140 words per minute

    private snackBar = inject(MatSnackBar);
    private translocoService = inject(TranslocoService);

    public openErrorSnackBar(translocoKey: string, options?: Record<string, string | number>): void {
        this.openSnackBar(translocoKey, SnackBarTypeEnum.ERROR, options);
    }

    public openInfoSnackBar(translocoKey: string, options?: Record<string, string | number>): void {
        this.openSnackBar(translocoKey, SnackBarTypeEnum.INFO, options);
    }

    public openPlainTextSnackBar(text: string, type: SnackBarTypeEnum): void {
        this.snackBar.open(text, '✖️', {
            duration: this.getDuration(text),
            panelClass: this.getPanelClass(type),
        });
    }

    public openSuccessSnackBar(translocoKey: string, options?: Record<string, string | number>): void {
        this.openSnackBar(translocoKey, SnackBarTypeEnum.SUCCESS, options);
    }

    public openWarningSnackBar(translocoKey: string, options?: Record<string, string | number>): void {
        this.openSnackBar(translocoKey, SnackBarTypeEnum.WARNING, options);
    }

    /**
     * @description get a good duration to read the message
     */
    private getDuration(message: string): number {
        const words = message.split(' ');
        const milliseconds = Math.round(words.length * SnackBarService.readingRatePerSecond * 1000);

        return Math.max(SnackBarService.minimalDuration, milliseconds);
    }

    private getPanelClass(type: SnackBarTypeEnum): string[] {
        switch (type) {
            case SnackBarTypeEnum.ERROR:
                return ['snackBar-error'];
            case SnackBarTypeEnum.SUCCESS:
                return ['snackBar-success'];
            case SnackBarTypeEnum.WARNING:
                return ['snackBar-warning'];
            default:
                return [];
        }
    }

    private openSnackBar(
        translocoKey: string,
        type: SnackBarTypeEnum,
        options?: Record<string, string | number>,
    ): void {
        this.translocoService
            .selectTranslate(translocoKey, options)
            .pipe(take(1))
            .subscribe((translateString: string) => {
                this.snackBar.open(translateString, '✖️', {
                    duration: this.getDuration(translateString),
                    panelClass: this.getPanelClass(type),
                });
            });
    }
}
